// AuthContext — v0.7.x (Auth + Saved Analyses + TOTP 2FA)
//
// Single source of truth for the signed-in user across the app. Wraps the
// Supabase auth session, listens for changes, and exposes email/password
// auth actions. Components read it via the `useAuth()` hook.
//
// MFA: TOTP (authenticator app) is wired up here as the second factor.
// `assuranceLevel` is exposed so the challenge gate can read aal1 vs aal2.
// Passkey (WebAuthn) methods are stubbed for the v0.7.x+ follow-on once
// Supabase's WebAuthn support leaves experimental.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { API_BASE_URL } from '../services/api';
import { identify as analyticsIdentify, reset as analyticsReset } from '../lib/analytics';
import { secondSession } from '../lib/onboardingFunnelAnalytics';
import { incrementSessionCount, isReturnSession } from '../lib/onboardingFunnelState';

// Shared helper for the recovery-code endpoints: pulls the current
// access token, POSTs (or GETs) to a Netlify function path under
// API_BASE_URL with the Bearer header, and returns { data, error }.
async function callMfaApi(path, init = {}) {
  if (!supabase) {
    return {
      data: null,
      error: { message: 'Supabase is not configured.' },
    };
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) {
    return {
      data: null,
      error: { message: 'Not signed in. Please sign in and try again.' },
    };
  }
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message:
            payload?.error ||
            `Request failed (HTTP ${response.status}).`,
          status: response.status,
          payload,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return {
      data: null,
      error: { message: err?.message || 'Network error.' },
    };
  }
}

const AuthContext = createContext(null);

// Consistent { data, error } shape for when Supabase isn't configured yet,
// so callers can handle it the same way they handle a real auth error.
const notConfigured = () =>
  Promise.resolve({
    data: null,
    error: {
      message:
        'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
    },
  });

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      // No client — nothing to load, drop straight out of the loading state.
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    // Hydrate from any persisted session on first mount.
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session ?? null);
        if (data.session?.user) {
          analyticsIdentify(data.session.user.id, { email: data.session.user.email });
        }
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setSession(null);
        setLoading(false);
      });

    // Keep context in sync with sign-in / sign-out / token-refresh events.
    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession ?? null);
      if (event === 'SIGNED_IN' && newSession?.user) {
        analyticsIdentify(newSession.user.id, { email: newSession.user.email });
        // Onboarding funnel — Second Session (funnel step 10, return-usage
        // signal). Fires exactly once, when this user's session count reaches 2.
        if (typeof window !== 'undefined') {
          const count = incrementSessionCount(window.localStorage, newSession.user.id);
          if (isReturnSession(count)) secondSession();
        }
      } else if (event === 'SIGNED_OUT') {
        analyticsReset();
      }
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(() => {
    const user = session?.user ?? null;

    return {
      session,
      user,
      loading,
      isConfigured: isSupabaseConfigured,
      // Authenticator Assurance Level from the access token, when present.
      // 'aal1' = password only, 'aal2' = a second factor was verified.
      // Used later to gate the 2FA challenge step.
      assuranceLevel: session?.user?.aal ?? null,

      signUp: (email, password) =>
        supabase
          ? supabase.auth.signUp({
              email,
              password,
              options: { emailRedirectTo: `${window.location.origin}/login` },
            })
          : notConfigured(),

      signIn: (email, password) =>
        supabase
          ? supabase.auth.signInWithPassword({ email, password })
          : notConfigured(),

      // SSO / social login. `provider` is a Supabase provider id —
      // 'google' is wired up now; 'apple' and 'azure' (Microsoft) follow
      // once their dashboard credentials are configured. Supabase handles
      // the redirect to the provider; the browser comes back to
      // /auth/callback, where detectSessionInUrl finishes the sign-in.
      signInWithOAuth: (provider) =>
        supabase
          ? supabase.auth.signInWithOAuth({
              provider,
              options: { redirectTo: `${window.location.origin}/auth/callback` },
            })
          : notConfigured(),

      signOut: () =>
        supabase ? supabase.auth.signOut() : notConfigured(),

      resetPassword: (email) =>
        supabase
          ? supabase.auth.resetPasswordForEmail(email, {
              redirectTo: `${window.location.origin}/login`,
            })
          : notConfigured(),

      // ---- TOTP 2FA methods (v0.7.x) ----
      //
      // Supabase MFA flow (TOTP):
      //   1. enroll({ factorType: 'totp' })  → creates an UNVERIFIED factor,
      //      returns { id, totp: { qr_code, secret } }
      //   2. challenge({ factorId })          → creates a one-time challenge
      //   3. verify({ factorId, challengeId, code }) → activates the factor
      //      and (during sign-in) upgrades the session to aal2
      //
      // listFactors() returns both 'verified' and 'unverified' factors —
      // callers that gate on "user has real 2FA" must filter on status.

      // Start TOTP enrollment. Returns { data: { id, type, totp: { qr_code, secret } }, error }.
      // The QR code is a data URL; the secret is for manual entry.
      //
      // friendlyName note: Supabase enforces friendly_name uniqueness per
      // user/factor type. If we omit it, Supabase stores empty string,
      // and ANY stale unverified factor (e.g., from a prior aborted
      // enrollment that the in-browser cleanup couldn't see at aal1)
      // collides with our new call as `mfa_factor_name_conflict`.
      // Callers should pass something unique — the enrollment modal
      // does this with a timestamp-derived name.
      enrollTotp: (friendlyName) => {
        if (!supabase) return notConfigured();
        const name =
          friendlyName ||
          `Authenticator (${new Date().toISOString().replace(/\.\d+Z$/, 'Z')})`;
        return supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: name,
        });
      },

      // Verify a TOTP code against a specific factor.
      // Called during enrollment (to flip the factor from unverified → verified)
      // and during sign-in (to upgrade the session from aal1 → aal2).
      // Returns Supabase's { data, error } shape directly.
      verifyTotp: async (factorId, code) => {
        if (!supabase) return notConfigured();
        if (!factorId) {
          return { data: null, error: { message: 'Missing factor id.' } };
        }
        if (!code || !/^\d{6}$/.test(code)) {
          return { data: null, error: { message: 'Enter the 6-digit code from your authenticator app.' } };
        }
        try {
          // challengeAndVerify combines mfa.challenge + mfa.verify in one call.
          // It works for both the enrollment-activation step and the sign-in
          // challenge step — Supabase handles the distinction server-side.
          return await supabase.auth.mfa.challengeAndVerify({ factorId, code });
        } catch (err) {
          return { data: null, error: err };
        }
      },

      // List ALL enrolled MFA factors (verified + unverified).
      // Factor shape: { id, friendly_name, factor_type, created_at, status }.
      // Most UI surfaces want listVerifiedMfaFactors() below; this one is
      // intended for the enrollment modal so it can clean up stuck factors.
      listMfaFactors: async () => {
        if (!supabase) return notConfigured();
        try {
          const { data, error } = await supabase.auth.mfa.listFactors();
          if (error) return { data: null, error };
          // Newer supabase-js versions return { totp, phone, all } —
          // older versions return { factors }. Cover both.
          const factors =
            data?.all ??
            data?.factors ??
            [...(data?.totp ?? []), ...(data?.phone ?? [])];
          return { data: factors, error: null };
        } catch (err) {
          return { data: null, error: err };
        }
      },

      // Same as above but filtered to factors Supabase considers real.
      // This is what the sign-in challenge gate and the Account page should
      // read — unverified factors are abandoned enrollment attempts, not
      // active second factors.
      listVerifiedMfaFactors: async () => {
        if (!supabase) return notConfigured();
        try {
          const { data, error } = await supabase.auth.mfa.listFactors();
          if (error) return { data: null, error };
          const factors =
            data?.all ??
            data?.factors ??
            [...(data?.totp ?? []), ...(data?.phone ?? [])];
          return {
            data: factors.filter((f) => f?.status === 'verified'),
            error: null,
          };
        } catch (err) {
          return { data: null, error: err };
        }
      },

      // Remove an enrolled factor (unenroll). Returns { data, error }.
      removeMfaFactor: (factorId) =>
        supabase
          ? supabase.auth.mfa.unenroll({ factorId })
          : notConfigured(),

      // Clean up any unverified TOTP factors before starting a fresh
      // enrollment. Best-effort: returns counts of inspected/removed
      // factors plus any failures so callers can log them. Per-factor
      // failures used to be swallowed silently — they now surface as
      // console warnings so we can see why a factor wouldn't budge
      // (often aal1 + Supabase MFA policies require aal2 to unenroll).
      cleanupUnverifiedFactors: async () => {
        if (!supabase) return { data: { removed: 0, failures: [] }, error: null };
        try {
          const { data, error } = await supabase.auth.mfa.listFactors();
          if (error) {
            console.warn('cleanupUnverifiedFactors: listFactors failed', error);
            return { data: { removed: 0, failures: [] }, error };
          }
          const factors =
            data?.all ??
            data?.factors ??
            [...(data?.totp ?? []), ...(data?.phone ?? [])];
          const stuck = factors.filter((f) => f?.status !== 'verified');

          let removed = 0;
          const failures = [];
          for (const f of stuck) {
            const { error: unenrollError } = await supabase.auth.mfa.unenroll({
              factorId: f.id,
            });
            if (unenrollError) {
              console.warn(
                'cleanupUnverifiedFactors: unenroll failed for',
                f.id,
                unenrollError
              );
              failures.push({
                id: f.id,
                factor_type: f.factor_type,
                status: f.status,
                message: unenrollError.message,
              });
            } else {
              removed += 1;
            }
          }
          return { data: { removed, failures }, error: null };
        } catch (err) {
          console.warn('cleanupUnverifiedFactors: unexpected error', err);
          return { data: { removed: 0, failures: [] }, error: err };
        }
      },

      // ---- Passkey (WebAuthn) foundation — v0.7.x+ ----
      //
      // Supabase's WebAuthn support is still flagged experimental as of
      // May 2026. These stubs give the Account page somewhere consistent
      // to call once it goes GA — adding the real implementation is then
      // a contained change inside AuthContext, not a refactor across the
      // app. Each stub returns the canonical { data, error } shape with
      // an explanatory error so the UI can render a "coming soon" state
      // without crashing.

      enrollPasskey: async (friendlyName) => {
        if (!supabase) return notConfigured();
        try {
          // Future GA path:
          //   return await supabase.auth.mfa.enroll({
          //     factorType: 'webauthn',
          //     friendlyName,
          //   });
          return {
            data: null,
            error: {
              message:
                'Passkey enrollment is not available yet. Supabase WebAuthn is still in experimental release; this app will enable it once it ships GA.',
              code: 'passkey_not_available',
            },
          };
        } catch (err) {
          return { data: null, error: err };
        }
      },

      verifyPasskey: async (factorId) => {
        if (!supabase) return notConfigured();
        return {
          data: null,
          error: {
            message:
              'Passkey verification is not available yet. Supabase WebAuthn is still in experimental release.',
            code: 'passkey_not_available',
          },
        };
      },

      // Quick boolean helper for the sign-in challenge gate — once
      // passkeys are wired up, the same gate will treat them as a valid
      // factor without any per-call branching.
      hasPasskeys: async () => {
        if (!supabase) return { data: false, error: null };
        try {
          const { data, error } = await supabase.auth.mfa.listFactors();
          if (error) return { data: false, error };
          const factors =
            data?.all ??
            data?.factors ??
            [...(data?.totp ?? []), ...(data?.phone ?? [])];
          const hasVerifiedPasskey = factors.some(
            (f) => f?.factor_type === 'webauthn' && f?.status === 'verified'
          );
          return { data: hasVerifiedPasskey, error: null };
        } catch (err) {
          return { data: false, error: err };
        }
      },

      // ---- Recovery (backup) codes — v0.7.x ----
      //
      // Issue, status, and consume operations all go through Netlify
      // functions backed by the service-role key, because:
      //   · only hashes live in Supabase — the plain codes need to be
      //     generated server-side and returned to the browser exactly
      //     once at enrollment / regeneration time
      //   · consuming a code has destructive side effects (deletes the
      //     TOTP factor + invalidates the other codes), which the
      //     anon/authenticated client roles aren't allowed to do
      //
      // See `netlify/functions/mfa-codes-*.js` for the server side.

      // Returns { data: { codes: string[], count }, error }. The plain
      // codes appear in `data.codes` exactly once — show them to the
      // user immediately and never store them client-side.
      generateRecoveryCodes: () =>
        callMfaApi('/mfa-codes-generate', { method: 'POST' }),

      // Returns { data: { total, remaining }, error } so callers (the
      // Account page) can render "N of 10 codes remaining" without
      // touching the codes themselves.
      getRecoveryCodeStatus: () =>
        callMfaApi('/mfa-codes-status', { method: 'GET' }),

      // Consume one recovery code. On success the user's TOTP factor
      // is deleted server-side AND the remaining recovery codes are
      // invalidated (industry-standard recovery semantics). Returns
      // { data: { success, message }, error }.
      consumeRecoveryCode: (code) =>
        callMfaApi('/mfa-codes-consume', {
          method: 'POST',
          body: JSON.stringify({ code }),
        }),
    };
  }, [session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}

export default AuthContext;
